import { EVENT_INSTANCE_TYPES } from 'settings/constants/service'
import { convertUnifiedDaysToForm } from './dataTransforms'

/**
 * Parse serviceInterval from various formats to form string format
 * @param {string|Object} serviceInterval - Service interval data
 * @returns {string} Service interval in 'Interval-Per' format
 */
export const parseServiceIntervalForDialog = serviceInterval => {
  if (!serviceInterval) {
    return 'Week-1' // Default value
  }

  if (typeof serviceInterval === 'object' && serviceInterval !== null) {
    // Convert object format to string format: {recurrenceInterval}-{recurrencePer}
    const interval = serviceInterval.recurrenceInterval || 'Week'
    const per = serviceInterval.recurrencePer || '1'
    return `${interval}-${per}`
  }

  if (typeof serviceInterval === 'string') {
    // Already in string format, pass through unchanged
    return serviceInterval
  }

  return 'Week-1' // Fallback
}

/**
 * Initialize and normalize arrays for non-weekly recurrence parameters
 * @param {Object} initialData - Initial data object
 * @param {number} frequency - Frequency number
 * @returns {Object} Object with normalized arrays
 */
export const initializeNonWeeklyArrays = (initialData, frequency) => {
  // Handle direct prop values or arrays - always create arrays
  let eventOccurrence = []
  if (Array.isArray(initialData.eventOccurrence)) {
    eventOccurrence = [...initialData.eventOccurrence]
  } else if (initialData.eventOccurrence !== undefined) {
    eventOccurrence = [initialData.eventOccurrence]
  }

  let dayOfTheWeek = []
  if (Array.isArray(initialData.dayOfTheWeek)) {
    dayOfTheWeek = [...initialData.dayOfTheWeek]
  } else if (initialData.dayOfTheWeek !== undefined) {
    dayOfTheWeek = [initialData.dayOfTheWeek]
  }

  let dayOfTheMonth = []
  if (Array.isArray(initialData.dayOfTheMonth)) {
    dayOfTheMonth = [...initialData.dayOfTheMonth]
  } else if (initialData.dayOfTheMonth !== undefined) {
    dayOfTheMonth = [initialData.dayOfTheMonth]
  }

  // Normalize arrays to correct length with proper defaults
  const normalizeArray = (arr, defaultValue, targetLength) => {
    const result = [...arr]
    // Fill to target length
    while (result.length < targetLength) {
      result.push(defaultValue)
    }
    // Trim if too long
    return result.slice(0, targetLength)
  }

  return {
    eventOccurrence: normalizeArray(eventOccurrence, '', frequency),
    dayOfTheWeek: normalizeArray(dayOfTheWeek, '', frequency),
    dayOfTheMonth: normalizeArray(dayOfTheMonth, '', frequency),
  }
}

/**
 * Convert initial data into dialog form state format
 * @param {Object} initialData - Initial recurrence configuration data
 * @param {string} initialData.frequency - Number of occurrences per interval (e.g., "3")
 * @param {Object} initialData.serviceInterval - Service interval configuration
 * @param {string} initialData.serviceInterval.recurrenceInterval - Interval type (e.g., "Week", "Month")
 * @param {string} initialData.serviceInterval.recurrencePer - Frequency multiplier (e.g., "1")
 * @param {Object} initialData.daysStructure - Days of week selection structure
 * @param {string} initialData.eventInstanceType - Type of event instance
 * @param {number|number[]} initialData.eventOccurrence - Week occurrence(s) in month
 * @param {string|string[]} initialData.dayOfTheWeek - Day(s) of week
 * @param {number|number[]} initialData.dayOfTheMonth - Day(s) of month
 * @returns {Object} Dialog state object with normalized form data
 */
export const getDialogInitialState = (initialData = {}) => {
  // Convert daysStructure to form format
  const daysOfWeekForm = initialData.daysStructure ? convertUnifiedDaysToForm(initialData.daysStructure) : convertUnifiedDaysToForm({})

  // Parse serviceInterval to form format
  const serviceIntervalValue = parseServiceIntervalForDialog(initialData.serviceInterval)

  // Get frequency for array initialization
  const frequency = parseInt(initialData.frequency, 10) || 1

  // Initialize and normalize non-weekly recurrence arrays
  const { eventOccurrence, dayOfTheWeek, dayOfTheMonth } = initializeNonWeeklyArrays(initialData, frequency)

  return {
    recurrenceFrequency: initialData.frequency || '1',
    serviceInterval: serviceIntervalValue,
    daysOfWeek: daysOfWeekForm,
    eventInstanceType: initialData.eventInstanceType || EVENT_INSTANCE_TYPES.DAY_OF_WEEK,
    eventOccurrence,
    dayOfTheWeek,
    dayOfTheMonth,
  }
}
